import { useCallback, useRef, useState } from "react";

type MutationResult<T> =
  { ok: true; value: T } | { ok: false; reason: "busy" | "failed" };

type MutationOptions = {
  rethrow?: boolean;
};

export const useClosingTimeRequestMutation = (
  onError: (error: unknown) => void,
) => {
  const [isMutating, setIsMutating] = useState(false);
  const mutationLockRef = useRef(false);

  const runMutation = useCallback(
    async <T>(
      operation: () => Promise<T>,
      options: MutationOptions = {},
    ): Promise<MutationResult<T>> => {
      if (mutationLockRef.current) {
        return { ok: false, reason: "busy" };
      }

      mutationLockRef.current = true;
      setIsMutating(true);
      try {
        return { ok: true, value: await operation() };
      } catch (error) {
        onError(error);
        if (options.rethrow) {
          throw error;
        }
        return { ok: false, reason: "failed" };
      } finally {
        mutationLockRef.current = false;
        setIsMutating(false);
      }
    },
    [onError],
  );

  return { isMutating, runMutation };
};
